import React, { Component } from 'react';
import _ from 'underscore';
import { titleize, humanize } from 'underscore.string';
import unitConverter from 'convert-units';
import ProTip from 'components/ui/ProTip';
import Badge from 'components/v2/Badge';
import {
  getStatus,
  giveCarrierOffer,
  giveDriverOffer,
} from 'utils/MarketplaceAPI';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import Pagination from 'components/v2/PaginationV2';
import ShipmentsActionCreators from 'actions/ShipmentsActionCreators';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cargomatic/ui';
import { ChevronDown } from 'lucide-react';
import formatMoney from 'utils/formatters/Money';
import { Translation, withTranslation } from 'react-i18next';
import { withLocaleContext } from 'providers/locale-settings-context';
import { colors } from 'constants/colors';
import { sluggify } from 'utils/localization/utils';
import { getEquipmentTypesForShipmentTypes } from 'constants/EquipmentTypes';
import { showToast } from 'stores/ToastStore';
import { DRIVER_SEARCH_DISTANCE_OPTIONS } from './mappings';

import ConfirmAssignCarrierModal from './ConfirmAssignCarrierModal';
import { checkShorthaulAppointmentNeeded } from 'helpers/shipments';

const defaultFormatter = (cell) => {
  return (
    <div className="py-2">
      <span>{cell || ''}</span>
    </div>
  );
};
const equipmentFormatter = (cell) => {
  const message = cell || 'UNKNOWN';
  return (
    <div className="py-2">
      <span className="bg-blue-light text-white text-xs rounded py-1 px-3">
        <Translation Translation>
          {(t) => titleize(t(sluggify(message)))}
        </Translation>
      </span>
    </div>
  );
};

const distanceFromOfferFormatter = (cell) => {
  const formattedDistance = unitConverter(cell)
    .from('m')
    .to(DEFAULT_DISTANCE_UNITS);
  return (
    <div className="py-2">
      <span>
        {formattedDistance.toFixed(2)} {DEFAULT_DISTANCE_UNITS}
      </span>
    </div>
  );
};

const statusFormatter = (cell, row) => {
  const hasOffer =
    row && row.hasOffer ? (
      <div className="py-2">
        <span className="px-2 py-1 text-white rounded bg-green">
          <Translation>{(t) => t('HAS_OFFER')}</Translation>
        </span>
      </div>
    ) : (
      <div className="py-2">
        <Translation>{(t) => t(sluggify(cell))}</Translation>
      </div>
    );
  if (hasOffer) return hasOffer;

  const message = cell || (
    <Translation>{(t) => t('ELIGIBLE_FOR_OFFER')}</Translation>
  );
  return <div className="py-2">{message}</div>;
};

const driverFormatter = (cell, row) => {
  if (!cell) return null;
  return (
    <div className="py-2">
      <a
        className=""
        target="_blank"
        href={`/users/${row.driverId}`}
        rel="noreferrer"
      >
        {humanize(cell.replace('&amp;', '&'))}
      </a>
    </div>
  );
};

const carrierFormatter = (cell, row) => {
  if (!cell) return null;
  return (
    <div className="py-2">
      <a
        className=""
        target="_blank"
        href={`/users/${row.carrierId}`}
        rel="noreferrer"
      >
        {humanize(cell.replace('&amp;', '&'))}
      </a>
    </div>
  );
};

class MarketplaceStatus extends Component {
  constructor(props) {
    super(props);

    this.state = {
      assigning: false,
      pages: 0,
      items: [],
      count: 0,
      filters: {
        distance: '20',
        authorized: true,
        exclusive_carriers: true,
        carriers: props.shipment.shipper.carriers,
        near: props.shipment.origin[0].coords,
        truck_type: 'All',
        per_page: 5,
        page: 1,
        paginate: true,
      },
      columns: this.getColumns(props.isDrayage),
    };

    this.query = _.debounce(this.query, 500);
    this.query();
  }

  getEquipmentTypeList() {
    const { shipment } = this.props;
    const selectedShipmentTypes = Array.isArray(shipment?.shipment_type)
      ? shipment.shipment_type
      : [shipment?.shipment_type];
    const types =
      getEquipmentTypesForShipmentTypes(selectedShipmentTypes) || [];
    return [
      { value: 'All', localizationSlug: 'ALL' },
      ...types.map((equipmentType) => ({
        localizationSlug: equipmentType.label,
        value: equipmentType.label,
      })),
    ];
  }

  canDriverAssign = () => {
    const { assigning } = this.state;
    const { current, payout_exists } = this.props.shipment;
    if (assigning) return false;
    if (!current) return false;
    if (payout_exists === true) return false;

    return true;
  };

  preferenceFormatter = (cell, row) => {
    const { t } = this.props;
    const { isPreferred, isExcluded } = row;
    let badgeColor = '';
    let preference = null;
    if (isPreferred) {
      badgeColor = colors.green;
      preference = t('CARRIER_PREFERRED');
    } else if (isExcluded) {
      badgeColor = colors.red;
      preference = t('CARRIER_EXCLUDED');
    }
    return preference ? <Badge color={badgeColor}>{preference}</Badge> : null;
  };

  assignDriver = ({ driverId }) => {
    this.setState({ assigning: true }, () => {
      ShipmentsActionCreators.assignDriver(this.props.shipment, {
        _id: driverId,
        _type: 'Driver',
      });
      setTimeout(() => {
        this.setState({ assigning: false });
      }, 1000);
    });
  };

  getColumns = (isDrayage) => {
    const { t } = this.props;
    const columns = [
      {
        dataField: 'driverName',
        text: t('DRIVER'),
        formatter: driverFormatter,
      },
      {
        dataField: 'preference',
        text: titleize(t('PREFERENCE')),
        formatter: (cell, row) => this.preferenceFormatter(cell, row),
      },
      {
        dataField: 'driverPhoneNumber',
        text: t('DRIVER_CONTACT'),
        formatter: defaultFormatter,
      },
      {
        dataField: 'vehicle',
        text: t('DRIVER_VEHICLE'),
        formatter: equipmentFormatter,
      },
      {
        dataField: 'distanceFromOffer',
        text: t('FROM_ORIGIN'),
        formatter: distanceFromOfferFormatter,
      },
      {
        dataField: 'carrierName',
        text: titleize(t('CARRIER', { count: 1 })),
        formatter: carrierFormatter,
      },
      {
        dataField: 'carrierPhoneNumber',
        text: t('CARRIER_CONTACT'),
        formatter: defaultFormatter,
      },
      {
        dataField: 'reason',
        text: titleize(t('STATUS')),
        formatter: statusFormatter,
      },
      {
        dataField: '',
        text: titleize(t('ACTION')),
        formatter: (cell, row) => {
          const { hasOffer, driverId, isCarrierTrained, is_broker } = row;
          const { status = '', shipment_type = '' } =
            this.props?.shipment || {};
          const disableBtn = checkShorthaulAppointmentNeeded(
            shipment_type,
            status,
          );
          return (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {t('ACTIONS')}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={hasOffer || disableBtn}
                    onSelect={() => {
                      const fnParams = {
                        shipmentId: this.props.shipment._id,
                        driverId,
                        isCarrierTrained,
                        isResend: false,
                      };
                      if (is_broker) {
                        this.setState({
                          showConfirmModal: true,
                          brokerCarrierDriver: fnParams,
                          confirmModalFnOperator: this.giveOffer,
                        });
                      } else {
                        this.giveOffer(fnParams);
                      }
                    }}
                  >
                    {titleize(t('OFFER', { count: 1 }))}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!hasOffer || disableBtn}
                    onSelect={() => {
                      const fnParams = {
                        shipmentId: this.props.shipment._id,
                        driverId,
                        isCarrierTrained,
                        isResend: true,
                      };
                      if (is_broker) {
                        this.setState({
                          showConfirmModal: true,
                          brokerCarrierDriver: fnParams,
                          confirmModalFnOperator: this.giveOffer,
                        });
                      } else {
                        this.giveOffer(fnParams);
                      }
                    }}
                  >
                    {t('RESEND')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!this.canDriverAssign() || disableBtn}
                    onSelect={() => {
                      const fnParams = { driverId };
                      if (is_broker) {
                        this.setState({
                          showConfirmModal: true,
                          brokerCarrierDriver: fnParams,
                          confirmModalFnOperator: this.assignDriver,
                        });
                      } else {
                        this.assignDriver(fnParams);
                      }
                    }}
                  >
                    {t('ASSIGN')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];

    const drayageColumns = [
      {
        dataField: 'carrierName',
        text: titleize(t('CARRIER', { count: 1 })),
        formatter: carrierFormatter,
      },
      {
        dataField: 'preference',
        text: titleize(t('PREFERENCE')),
        formatter: (cell, row) => this.preferenceFormatter(cell, row),
      },
      {
        dataField: 'price',
        text: t('RATE', { count: 1 }),
        formatter: (cell) => (
          <div className="py-2 text-right">
            <span>{formatMoney(cell)}</span>
          </div>
        ),
      },
      {
        dataField: 'carrierPhoneNumber',
        text: t('CARRIER_CONTACT'),
        formatter: defaultFormatter,
      },
      {
        dataField: 'reason',
        text: titleize(t('STATUS')),
        formatter: statusFormatter,
      },
      {
        dataField: '',
        text: titleize(t('ACTION')),
        formatter: (cell, row) => {
          const { hasOffer, carrierId, isCarrierTrained, is_broker } = row;
          return (
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    {t('ACTIONS')}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={hasOffer}
                    onSelect={() => {
                      const fnParams = {
                        offerId: this.props.shipment._id,
                        carrierId,
                        isCarrierTrained,
                        isResend: false,
                      };
                      if (is_broker) {
                        this.setState({
                          showConfirmModal: true,
                          brokerCarrierDriver: fnParams,
                          confirmModalFnOperator: this.giveDrayageOffer,
                        });
                      } else {
                        this.giveDrayageOffer(fnParams);
                      }
                    }}
                  >
                    {titleize(t('OFFER', { count: 1 }))}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={!hasOffer}
                    onSelect={() => {
                      const fnParams = {
                        offerId: this.props.shipment._id,
                        carrierId,
                        isCarrierTrained,
                        isResend: true,
                      };
                      if (is_broker) {
                        this.setState({
                          showConfirmModal: true,
                          brokerCarrierDriver: fnParams,
                          confirmModalFnOperator: this.giveDrayageOffer,
                        });
                      } else {
                        this.giveDrayageOffer(fnParams);
                      }
                    }}
                  >
                    {t('RESEND')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ];

    if (isDrayage) {
      return drayageColumns;
    }

    return columns;
  };

  giveOffer = async ({ shipmentId, driverId, isCarrierTrained, isResend }) => {
    const { t } = this.props;
    if (!isCarrierTrained)
      return showToast(
        t('CARRIER_IS_NOT_TRAINED'),
        t('CARRIER_IS_NOT_TRAINED'),
        'error',
      );
    const result = await giveDriverOffer(shipmentId, driverId, isResend);
    if (result && result.success) {
      this.query();
    }
  };

  giveDrayageOffer = async ({
    offerId,
    carrierId,
    isCarrierTrained,
    isResend,
  }) => {
    const { t } = this.props;
    if (!isCarrierTrained)
      return showToast(
        t('CARRIER_IS_NOT_TRAINED'),
        t('CARRIER_IS_NOT_TRAINED'),
        'error',
      );
    const result = await giveCarrierOffer(offerId, carrierId, isResend);
    if (result && result.carrier) {
      this.query();
    }
  };

  UNSAFE_componentWillReceiveProps(newProps) {
    if (
      newProps.shipment &&
      newProps.shipment._id !== this.props.shipment._id
    ) {
      this.query();
    }
  }

  query = async () => {
    const { t, localeData } = this.props;
    const { filters } = this.state;
    const {
      shipment: { _id, shipment_type },
      isDrayage,
    } = this.props;

    try {
      const filtersClone = _.clone(filters);
      if (filtersClone.customerName === '') {
        delete filtersClone.customerName;
      }
      if (isDrayage) {
        delete filtersClone.near;
      }

      if (filtersClone.distance) {
        filtersClone.distance = unitConverter(filtersClone.distance)
          .from(localeData.distance_units)
          .to('m');
      }

      const result = await getStatus(_id, filtersClone, shipment_type);

      const update = {
        count: result.totalCount,
        pages: result.totalPages,
        items: result.items,
        error: null,
      };

      this.setState(update);
    } catch (e) {
      this.setState({
        error: t('FAILED_TO_GET_MARKETPLACE_STATUS', { message: e.message }),
      });
    }
  };

  onCustomerNameChange = (value) => {
    const { filters } = this.state;

    if (!filters.customerName || filters.customerName === '') {
      filters.distance = '150';
      filters.truck_type = 'All';
      filters.exclusive_carriers = false;
      filters.carriers = [];
    }

    filters.customerName = value;
    filters.page = 1;
    this.setState({ filters }, () => {
      this.query();
    });
  };

  handleDistanceSelect(value) {
    const { filters } = this.state;
    filters.distance = value;
    this.setState({ filters }, () => {
      this.query();
    });
  }

  withinDistanceOptions() {
    return DRIVER_SEARCH_DISTANCE_OPTIONS.marketplace[REGION_CODE];
  }

  handleTruckTypeSelect = (type) => {
    const { filters } = this.state;
    filters.truck_type = type?.localizationSlug;
    this.setState({ filters }, () => {
      this.query();
    });
  };

  handleExclusiveCarrierSelect = (checked) => {
    const { filters } = this.state;
    filters.exclusive_carriers = checked;
    filters.carriers = checked ? this.props.shipment.shipper.carriers : [];
    this.setState({ filters }, () => {
      this.query();
    });
  };

  onPaginationSelect = (page) => {
    const { filters } = this.state;
    filters.page = page;
    this.setState({ filters }, () => {
      this.query();
    });
  };

  render() {
    const {
      items,
      pages,
      filters: { page, customerName },
    } = this.state;
    const { isDrayage, t, localeData } = this.props;

    const message = isDrayage
      ? t('MARKETPLACE_PROTIP_DRAYAGE')
      : t('MARKETPLACE_PROTIP_NON_DRAYAGE');

    return (
      <div className="space-y-4">
        <ProTip message={message} />
        <div className="flex flex-wrap gap-4">
          <div className="w-1/3">
            <CargomaticInput
              className=""
              type="checkbox"
              label={t('EXCLUSIVE_CARRIERS_ONLY')}
              id="exclusive_carriers"
              name="exclusive_carriers"
              checked={this.state.filters.exclusive_carriers}
              onChange={(checked) => this.handleExclusiveCarrierSelect(checked)}
            />
          </div>
          <div className="w-1/3">
            {!isDrayage && (
              <CargomaticSelect
                className="w-3/4"
                label={t('EQUIPMENT_TYPE')}
                textKey="localizationSlug"
                valueKey="value"
                items={this.getEquipmentTypeList()}
                disabled={false}
                value={this.state.filters.truck_type}
                onSelect={(truck) => this.handleTruckTypeSelect(truck)}
              />
            )}
          </div>
          <div className="w-1/3">
            {!isDrayage && (
              <CargomaticSelect
                className="w-3/4"
                label={t('WITHIN_DISTANCE', {
                  distanceUnit: localeData.distance_units,
                })}
                items={this.withinDistanceOptions()}
                disabled={false}
                value={this.state.filters.distance}
                onSelect={(distance) => this.handleDistanceSelect(distance)}
              />
            )}
          </div>
        </div>
        <div className="w-full md:w-80 mb-4">
          <CargomaticInput
            value={customerName || ''}
            placeholder={
              isDrayage
                ? t('SEARCH_BY_CARRIER_NAME')
                : t('SEARCH_BY_DRIVER_NAME')
            }
            label={t('SEARCH')}
            info={
              isDrayage
                ? t('DISTRIBUTION_STATUS_CARRIER_SEARCH')
                : t('DISTRIBUTION_STATUS_DRIVER_SEARCH')
            }
            onChange={this.onCustomerNameChange}
            name="customer"
            type="text"
          />
        </div>
        {items && items.length > 0 && (
          <div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {this.state.columns.map((col) => (
                      <TableHead
                        key={col.dataField || col.text}
                        className="whitespace-nowrap"
                      >
                        {col.text}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row, rowIndex) => {
                    const keyField = isDrayage ? 'carrierId' : 'driverId';
                    return (
                      <TableRow key={row[keyField] || rowIndex}>
                        {this.state.columns.map((col) => (
                          <TableCell
                            key={col.dataField || col.text}
                            className="whitespace-nowrap"
                          >
                            {col.formatter
                              ? col.formatter(row[col.dataField], row)
                              : row[col.dataField] || ''}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {pages > 1 && (
              <Pagination
                perPage={this.state.filters.per_page}
                page={page}
                total={this.state.count}
                onPageClick={this.onPaginationSelect}
                showCount={false}
              />
            )}
          </div>
        )}
        <ConfirmAssignCarrierModal
          isOpen={this.state.showConfirmModal}
          brokerCarrierDriver={this.state.brokerCarrierDriver}
          shipment={this.props.shipment}
          onConfirm={() => {
            this.state.confirmModalFnOperator(this.state.brokerCarrierDriver);
            this.setState({
              showConfirmModal: false,
              brokerCarrierDriver: false,
              confirmModalFnOperator: false,
            });
          }}
          onClose={() => this.setState({ showConfirmModal: false })}
        />
      </div>
    );
  }
}

export default withTranslation()(withLocaleContext(MarketplaceStatus));
